import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Phone } from 'lucide-react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/24255a550df67aeec175ac877a674e8bae385938fef42044bf92b3a8217b6427.png";

export default function LoginPage() {
  const { login, register, error } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);
    
    const result = await login(loginEmail, loginPassword);
    setIsLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (registerPassword !== registerConfirm) {
      setLocalError('Passwords do not match');
      return;
    }
    
    if (registerPassword.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    const result = await register(registerEmail, registerPassword, registerName);
    setIsLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors" data-testid="back-home-link">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-jakarta font-medium">Back to Home</span>
          </Link>
          <a href="tel:347-938-3278" className="flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors" data-testid="login-phone">
            <Phone className="h-4 w-4" />
            <span className="font-jakarta font-medium hidden sm:inline">347-938-3278</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Essex Street Contracting" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0F172A]">Client Portal</h1>
            <p className="font-jakarta text-[#475569] mt-2">Access your project estimates and documents</p>
          </div>

          {/* Auth Card */}
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 sm:p-8 shadow-sm">
            {displayError && (
              <Alert variant="destructive" className="mb-6" data-testid="auth-error">
                <AlertDescription>{displayError}</AlertDescription>
              </Alert>
            )}

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#F1F5F9]">
                <TabsTrigger 
                  value="login" 
                  className="font-jakarta data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-sm"
                  data-testid="login-tab"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="font-jakarta data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-sm"
                  data-testid="register-tab"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="font-jakarta text-[#0F172A]">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="font-jakarta text-[#0F172A]">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] font-jakarta font-semibold py-5 rounded-sm transition-all"
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="font-jakarta text-[#0F172A]">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Smith"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="font-jakarta text-[#0F172A]">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="font-jakarta text-[#0F172A]">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm" className="font-jakarta text-[#0F172A]">Confirm Password</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={registerConfirm}
                      onChange={(e) => setRegisterConfirm(e.target.value)}
                      required
                      className="rounded-sm border-[#E2E8F0] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="register-confirm-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] font-jakarta font-semibold py-5 rounded-sm transition-all"
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>

          {/* Footer note */}
          <p className="text-center text-[#475569] text-sm font-jakarta mt-6">
            Need help? Call us at{' '}
            <a href="tel:347-938-3278" className="text-[#D4AF37] hover:underline">
              347-938-3278
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
